"use client";

import { useCallback, useEffect, useState } from "react";

/** Phone. */
export const BREAKPOINT_SM = 640;
/** Mobile shell. Shared with the desk PWA. */
export const BREAKPOINT_MD = 900;
/** Right drawer may stay open. */
export const BREAKPOINT_LG = 1280;

/** Shared mobile layout breakpoint for the desk PWA. */
export const MOBILE_BREAKPOINT = BREAKPOINT_MD;

export type StudioBreakpoint = "sm" | "md" | "lg";

function readWidth(): number {
  if (typeof window === "undefined") return BREAKPOINT_LG;
  return window.innerWidth;
}

/** One breakpoint source. `isMobile` is below `md` (900). `isLg` is 1280 and up. */
export function useBreakpoint() {
  const [width, setWidth] = useState(readWidth);

  useEffect(() => {
    const queries = [
      window.matchMedia(`(max-width: ${BREAKPOINT_SM - 1}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINT_MD - 1}px)`),
      window.matchMedia(`(min-width: ${BREAKPOINT_LG}px)`),
    ];
    const sync = () => setWidth(readWidth());
    sync();
    for (const query of queries) query.addEventListener("change", sync);
    window.addEventListener("resize", sync);
    return () => {
      for (const query of queries) query.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);

  const name: StudioBreakpoint =
    width < BREAKPOINT_SM ? "sm" : width < BREAKPOINT_MD ? "md" : "lg";

  return {
    name,
    width,
    isSm: width < BREAKPOINT_SM,
    isMd: width < BREAKPOINT_MD,
    isMobile: width < BREAKPOINT_MD,
    isLg: width >= BREAKPOINT_LG,
  };
}

/** 0 = Agent, 1 = Files, 2 = Editor */
export type MobileTab = 0 | 1 | 2;

const TAB_KEY = "desk2-mobile-tab";

export function loadMobileTab(): MobileTab {
  if (typeof window === "undefined") return 0;
  const raw = sessionStorage.getItem(TAB_KEY);
  const n = raw == null ? 0 : Number(raw);
  if (n === 1 || n === 2) return n;
  return 0;
}

export function saveMobileTab(tab: MobileTab) {
  try {
    sessionStorage.setItem(TAB_KEY, String(tab));
  } catch {
    /* ignore */
  }
}

function readIsMobile(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
}

export function useMobileLayout() {
  // Initialize from the viewport on the client so the first paint matches mobile
  // chrome instead of flashing desktop structure after hydration.
  const [isMobile, setIsMobile] = useState(readIsMobile);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onMq = () => setIsMobile(mq.matches);
    onMq();
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setKeyboardOpen(false);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) return;

    const threshold = 80;
    const baseline = window.innerHeight;

    const onResize = () => {
      setKeyboardOpen(baseline - vv.height > threshold);
    };

    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, [isMobile]);

  const onComposerFocus = useCallback(() => {
    if (isMobile) setKeyboardOpen(true);
  }, [isMobile]);

  const onComposerBlur = useCallback(() => {
    if (!isMobile) return;
    window.setTimeout(() => {
      const active = document.activeElement;
      if (
        active instanceof HTMLTextAreaElement &&
        active.closest(".cursor-composer")
      ) {
        return;
      }
      const vv = window.visualViewport;
      const open = vv ? window.innerHeight - vv.height > 80 : false;
      setKeyboardOpen(open);
    }, 120);
  }, [isMobile]);

  return { isMobile, keyboardOpen, onComposerFocus, onComposerBlur };
}
